import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { inr, companyLogoUrl } from "@/lib/erp";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function PublicStatement() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/statement/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("This statement link is invalid or has expired."));
  }, [token]);

  if (err) return <div className="min-h-screen grid place-items-center text-slate-500 p-6 text-center" data-testid="public-statement-error">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  const c = data.customer || {};
  const co = data.company || {};
  const logo = companyLogoUrl(co);

  return (
    <div className="min-h-screen bg-slate-100 py-8" data-testid="public-statement-page">
      <div id="print-area" className="max-w-3xl mx-auto px-4">
        <div className="no-print flex justify-end mb-4">
          <Button size="sm" variant="print" onClick={() => window.print()} data-testid="public-statement-print"><Printer className="h-4 w-4 mr-1" /> Print / Download</Button>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="" style={{ maxHeight: "48px", maxWidth: "180px", objectFit: "contain" }} />}
              <div>
                <div className="font-heading text-lg font-bold">{co.name}</div>
                {co.gstin && <div className="text-[11px] text-slate-600">GSTIN: {co.gstin}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading font-bold">Account Statement</div>
              <div className="text-[11px] text-slate-600">{new Date().toLocaleDateString()}</div>
            </div>
          </div>
          <div className="py-4 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Statement For</div>
            <div className="font-semibold">{c.name}</div>
            {c.billing_address && <div className="text-sm text-slate-600">{c.billing_address}</div>}
          </div>
          <div className="grid grid-cols-3 gap-4 py-4 text-center">
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase text-slate-500">Total Billed</div><div className="font-bold tabular">{inr(data.total_debit)}</div></div>
            <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[10px] uppercase text-emerald-600">Collected</div><div className="font-bold tabular text-emerald-700">{inr(data.total_credit)}</div></div>
            <div className="rounded-lg bg-rose-50 p-3"><div className="text-[10px] uppercase text-rose-600">Outstanding</div><div className="font-bold tabular text-rose-700">{inr(data.outstanding)}</div></div>
          </div>
          <table className="w-full text-sm mt-2">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
              <th className="py-2 pr-2">Date</th><th className="py-2 pr-2">Type</th><th className="py-2 pr-2">Reference</th>
              <th className="py-2 text-right">Debit</th><th className="py-2 text-right">Credit</th><th className="py-2 text-right">Balance</th>
            </tr></thead>
            <tbody>
              {(data.entries || []).map((e, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 pr-2">{e.date}</td><td className="py-2 pr-2">{e.type}</td><td className="py-2 pr-2 font-mono text-xs">{e.ref}</td>
                  <td className="py-2 text-right font-mono">{e.debit ? inr(e.debit) : "—"}</td>
                  <td className="py-2 text-right font-mono text-emerald-600">{e.credit ? inr(e.credit) : "—"}</td>
                  <td className="py-2 text-right font-mono font-semibold">{inr(e.balance)}</td>
                </tr>
              ))}
              {(data.entries || []).length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No transactions</td></tr>}
            </tbody>
          </table>
          <div className="text-center text-[11px] text-slate-400 mt-6">Sent by {co.name}</div>
        </div>
      </div>
    </div>
  );
}
